// Modelled directly on Hedge-Fund-Tracking-System NavLineChart:
// monotone line, theme-aware axes, custom tooltip.
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { useTheme } from '../layout/theme-provider';
import { useSnapshots } from '../../hooks/useApi';
import { formatCurrency, formatTime } from '../../lib/utils';

interface Props {
  height?: number;
  /** Volatile-side label ('ETH', 'VIRTUAL', …). Default ETH. */
  coinLabel?: string;
}

function CustomTooltip({
  active,
  payload,
  label,
  coinLabel,
}: {
  active?: boolean;
  payload?: { value: number }[];
  label?: number;
  coinLabel: string;
}) {
  if (!active || !payload?.length || label === undefined) return null;
  return (
    <div className="rounded-lg border border-border bg-surface shadow-lg p-3 text-sm">
      <p className="text-muted-foreground mb-1">{formatTime(label)}</p>
      <p className="font-semibold text-foreground tabular-nums">
        {coinLabel}: {formatCurrency(payload[0]!.value)}
      </p>
    </div>
  );
}

export function PriceLineChart({ height = 240, coinLabel = 'ETH' }: Props) {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  const gridColor = isDark ? 'hsl(215 25% 27%)' : 'hsl(214 32% 91%)';
  const textColor = isDark ? 'hsl(215 20% 65%)' : 'hsl(215 16% 47%)';
  const lineColor = 'hsl(221 83% 53%)';

  const { data: snapshots } = useSnapshots(24);
  const formatted = (snapshots ?? []).map((s) => ({
    ts: s.ts,
    price: s.ethPrice,
  }));

  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={formatted} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={gridColor} vertical={false} />
        <XAxis
          dataKey="ts"
          type="number"
          domain={['dataMin', 'dataMax']}
          tickFormatter={formatTime}
          tick={{ fill: textColor, fontSize: 11 }}
          tickLine={false}
          axisLine={false}
          minTickGap={48}
        />
        <YAxis
          tick={{ fill: textColor, fontSize: 11 }}
          tickLine={false}
          axisLine={false}
          tickFormatter={(v: number) => `$${v.toFixed(Math.abs(v) < 10 ? 3 : 0)}`}
          width={56}
          domain={['auto', 'auto']}
        />
        <Tooltip content={<CustomTooltip coinLabel={coinLabel} />} cursor={{ stroke: gridColor }} />
        <Line
          type="monotone"
          dataKey="price"
          stroke={lineColor}
          strokeWidth={2}
          dot={false}
          activeDot={{ r: 4, strokeWidth: 0 }}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}
